import { inject, observer } from "mobx-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";
import { CreditApplicationState, Customer, ExtractFriendlyName, SimpleCustomer } from "src/model/Customer";

import {
    faBan,
    faExclamationTriangle,
    faExternalLinkSquareAlt,
    faPenSquare,
    faPercent,
    faTrashAlt,
    faTrophy,
    faUser,
    faFlag
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { HammerFallOutcome, HorseForSale, HorseForSalePurchase, HSTExemptionType } from "../../model/HorseForSale";
import CustomerSelect from "../common/CustomerSelect";
import { FinalBidAmountWarning } from "../common/FinalBidAmountWarning";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { InputGroup, TextareaGroup, InputPercentageGroup } from "../common/SimpleFormComponents";
import { errorMessage } from "../common/Toast";
import { CheckoutHorseHeader } from "./CheckoutHorsesHeader";
import { CheckoutTabs, EditableHorseForSalePurchase } from "./CheckoutTemplate";
import { SortFn } from "./CommonTableSorting";
import { CustomerLinkCheckout } from "../common/CustomerLinkCheckout";

interface Props {
    customerEndpoints?: CustomerEndpoints;

    winningBidder: Customer;
    currentHorseForSale: HorseForSale;

    //Form Action
    onChange: (currentHorseForSale: HorseForSale, purchasers: EditableHorseForSalePurchase.Fields) => void;
    horseForSalePurchaseList: EditableHorseForSalePurchase.Fields;

    showGoBackHorse: boolean;
    goToTab: (currentTab: string) => void;
    goBackHorse: () => void;
}

interface StatePage {
    wasValidated: boolean;
    showForm: boolean;
    editingPurshaserId: number | null;
    percentageMessage: string;
}

type State = StatePage & EditableHorseForSalePurchase.PurchaseFields;

type PreparedPurchaser = Pick<State, "purchaser" | "creditApplication" | "insuranceRecord" | "hstExemption">;

const DefaultState = () => {
    return {
        ...EditableHorseForSalePurchase.EmptyPurchaser(),
        wasValidated: false,
        showForm: false,
        editingPurshaserId: null,
        horseForSalePurchaseList: [],
        percentageMessage: ""
    };
};

@inject("customerEndpoints")
@observer
export default class CheckoutBuyers extends React.Component<Props, State> {
    state: State = DefaultState();

    private form: HTMLFormElement | null;

    submit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        event.stopPropagation();

        if (this.form && this.form.checkValidity() && this.formValidation()) {
            this.setState({ wasValidated: false });
            this.state.editingPurshaserId ? this.editPurchaser() : this.addPurchaser();
            this.clearForm();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    isWinningBidderAnAgentForThisPurchaser = (purchaser: Customer): boolean => {
        const winnerBidderId = this.props.winningBidder!.id;
        const currentHorseForSaleSaleId = this.props.currentHorseForSale.sale.id;

        return !!(
            purchaser.id !== winnerBidderId &&
            purchaser.delegatorAuthorizations.find(
                aa => aa.agent.id === winnerBidderId && aa.sale.id === currentHorseForSaleSaleId && aa.dateRevoked === null
            )
        );
    };

    preparePurchaser = async (customerId: number): Promise<PreparedPurchaser> => {
        const winningBidder = this.props.winningBidder;
        const purchaser = await this.props.customerEndpoints!.getCustomer(customerId);
        const newState: any = { purchaser };

        const activeCredit = purchaser.creditApplications.find(c => c.type === "PERMANENT" && c.state === CreditApplicationState.APPROVED) || null;
        const activeInsurance = purchaser.insuranceRecords.find(ir => ir.dateCancelled === null) || null;

        // Fill in credit with defaults but only if this is not the winning bidder, but an agent authorization is present
        if (customerId !== winningBidder.id && this.isWinningBidderAnAgentForThisPurchaser(purchaser)) {
            newState.creditApplication = activeCredit;
            // Fill in insurance with defaults, but only if credit was also granted
            if (activeCredit) {
                newState.insuranceRecord = activeInsurance;
            }
        } else if (customerId === winningBidder.id) {
            // If this is the winning bidder, then we direct this update to the purchase itself
            this.props.onChange(this.props.currentHorseForSale, {
                ...this.props.horseForSalePurchaseList,
                winningBidderCreditApplication: activeCredit,
                winningBidderInsuranceRecord: activeInsurance
            });
        }

        // Fill in HST exemption
        if (purchaser.firstNationsIdNumber) {
            newState.hstExemption = {
                exemption: HSTExemptionType.FN,
                notes: ""
            };
        } else {
            newState.hstExemption = {
                exemption: HSTExemptionType.NONE,
                notes: ""
            };
        }

        return newState as PreparedPurchaser;
    };

    clearPurchaser(): PreparedPurchaser {
        return {
            purchaser: null,
            creditApplication: null,
            insuranceRecord: null,
            hstExemption: EditableHorseForSalePurchase.EmptyHSTExemption()
        };
    }

    purchaserSelected = async (simplePurchaser: SimpleCustomer) => {
        try {
            if (simplePurchaser === null) {
                this.setState(this.clearPurchaser());
            } else {
                const newState = await this.preparePurchaser(simplePurchaser.id);
                this.setState(newState);
            }
        } catch (error) {
            errorMessage(error);
        }
    };

    addPurchaser = () => {
        this.props.onChange(this.props.currentHorseForSale, {
            ...this.props.horseForSalePurchaseList,
            buyers: [...this.props.horseForSalePurchaseList.buyers, EditableHorseForSalePurchase.PurchaseMakeCopy(this.state)]
        });
    };

    removePurchaser = (purchaser: SimpleCustomer) => {
        const isWinningBidder = purchaser.id === this.props.winningBidder.id;
        this.props.onChange(this.props.currentHorseForSale, {
            ...this.props.horseForSalePurchaseList,
            buyers: this.props.horseForSalePurchaseList.buyers.filter(hfsp => hfsp.purchaser!.id !== purchaser.id),
            // Clear  these three if we're removing the winning bidder
            winningBidderCreditApplication: isWinningBidder ? null : this.props.horseForSalePurchaseList.winningBidderCreditApplication,
            winningBidderOnTheSpotCreditApplication: isWinningBidder
                ? null
                : this.props.horseForSalePurchaseList.winningBidderOnTheSpotCreditApplication,
            winningBidderInsuranceRecord: isWinningBidder ? null : this.props.horseForSalePurchaseList.winningBidderInsuranceRecord
        });
        this.clearForm();
    };

    editPurchaser = () => {
        // const filteredPurchasers = this.props.horseForSalePurchaseList.filter((hfsp) => hfsp.purchaser!.id !== this.state.editingPurshaserId);
        // this.props.onChange(this.props.currentHorseForSale, [...filteredPurchasers, EditableHorseForSalePurchase.MakeCopy(this.state)]);
        this.props.onChange(this.props.currentHorseForSale, {
            ...this.props.horseForSalePurchaseList,
            buyers: [
                ...this.props.horseForSalePurchaseList.buyers.filter(hfsp => hfsp.purchaser!.id !== this.state.editingPurshaserId),
                EditableHorseForSalePurchase.PurchaseMakeCopy(this.state)
            ]
        });
    };

    submitBuyers = async () => {
        const winningBidder = this.props.winningBidder;
        const horseForSalePurchaserWithoutAgent = this.props.horseForSalePurchaseList.buyers.filter(
            hfsp => hfsp.purchaser!.id !== winningBidder.id && !winningBidder.agentAuthorizations.some(ca => ca.delegator.id === hfsp.purchaser!.id)
        );

        if (horseForSalePurchaserWithoutAgent.length > 0) {
            const goAhead = await SimpleConfirmation(
                <span>
                    <div className="text-left mb-3">
                        The following buyer(s) don't have an agent authorization on file for {ExtractFriendlyName(this.props.winningBidder)}. Note
                        that {ExtractFriendlyName(this.props.winningBidder)} will be ultimately liable for delinquent payments from these buyer(s).
                    </div>
                    <ul className="pl-3">
                        {horseForSalePurchaserWithoutAgent.map(hfsp => (
                            <li className="text-left" key={hfsp.purchaser!.id}>
                                {ExtractFriendlyName(hfsp.purchaser!)}
                            </li>
                        ))}
                    </ul>
                    <div className="text-left mb-3">Are you sure you'd like to proceed?</div>
                </span>
            );

            if (!goAhead) {
                return;
            }
        }

        // KB: Commented out since it looks like this is done with each change anyhow?
        // this.props.onChange(this.props.currentHorseForSale, this.props.horseForSalePurchaseList);
        this.props.goToTab(CheckoutTabs.CREDIT_AND_INSURANCE);
    };

    handleBidIn = async () => {
        const buyers = await Promise.all(
            this.props.currentHorseForSale.owners.map(async owner => {
                const newState = await this.preparePurchaser(owner.owner.id);
                return {
                    ...EditableHorseForSalePurchase.EmptyPurchaser(),
                    ...newState,
                    percentage: owner.percentage.toString()
                };
            })
        );
        this.props.onChange(this.props.currentHorseForSale, {
            ...this.props.horseForSalePurchaseList,
            buyers
        });
        this.clearForm();
    };

    handleAllWinningBidder = async () => {
        const newState = await this.preparePurchaser((this.props.currentHorseForSale.outcome! as HammerFallOutcome).winningBidder.id);
        this.props.onChange(this.props.currentHorseForSale, {
            ...this.props.horseForSalePurchaseList,
            buyers: [
                {
                    ...EditableHorseForSalePurchase.EmptyPurchaser(),
                    ...newState,
                    percentage: "100"
                }
            ]
        });
        this.clearForm();
    };

    clearForm = () => {
        this.setState({ ...EditableHorseForSalePurchase.EmptyPurchaser(), editingPurshaserId: null });
    };

    handleRenderPurchasers = (hfsp: EditableHorseForSalePurchase.PurchaseFields) => {
        const hfsPurchaser = hfsp.purchaser!;
        if (hfsPurchaser.id === this.props.winningBidder.id) {
            return <CustomerLinkCheckout page="overview" customer={hfsPurchaser} isWinningBider={true} />;
        } else {
            return <CustomerLinkCheckout page="overview" customer={hfsPurchaser} />;
        }
    };

    handleRenderAgentAuthorization = (hfsp: EditableHorseForSalePurchase.PurchaseFields) => {
        const hfsPurchaser = hfsp.purchaser!;
        if (hfsPurchaser.id !== this.props.winningBidder.id) {
            if (this.props.winningBidder.agentAuthorizations.some(ca => ca.delegator.id === hfsPurchaser.id)) {
                return "Yes";
            } else {
                return (
                    <>
                        <span className="text-danger">No</span>&nbsp;
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" />
                    </>
                );
            }
        } else {
            return "N/A";
        }
    };

    render() {
        const horseForSale = this.props.currentHorseForSale;
        const percentage = this.calculatePercentage();
        const percentageAt100 = percentage === 100;
        const showForm = percentageAt100 === false || (percentageAt100 === true && this.state.editingPurshaserId !== null);
        const data = this.props.horseForSalePurchaseList.buyers.sort(SortFn(this.props.winningBidder, this.props.currentHorseForSale.sale.id));

        const finalBidAmount = (horseForSale.outcome as HammerFallOutcome).finalBidAmount / 100;

        return (
            <div className="card shadow">
                <div className="card-header">
                    <CheckoutHorseHeader stepNumber={1} stepTitle="Buyer(s)" currentHorseForSale={horseForSale} />
                </div>
                <div className="card-body">
                    <div className="form-row">
                        <div className="col-12">
                            <div className="alert alert-warning text-center">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                                For a bid-in, please remember to check the buyer's account first for the correct charge of HST to the the Entry Fee
                                invoice.
                            </div>
                        </div>
                    </div>
                    <FinalBidAmountWarning finalBidAmount={finalBidAmount} />
                    <div className="form-row text-right">
                        <div className="col-12">
                            <button className="btn btn-info btn-sm mr-3" onClick={() => this.handleAllWinningBidder()}>
                                100% Winning Bidder
                            </button>
                            <button className="btn btn-info btn-sm" onClick={() => this.handleBidIn()}>
                                Bid in
                            </button>
                        </div>
                    </div>
                    {showForm && (
                        <div className="form-row pr-0">
                            <div className="col-12 pr-0">
                                <form
                                    id="purchasers"
                                    ref={form => (this.form = form)}
                                    onSubmit={e => this.submit(e)}
                                    className={"needs-validation mt-3" + (this.state.wasValidated ? " was-validated" : "")}
                                    noValidate={true}
                                >
                                    <div className="card mr-0" style={{ backgroundColor: "#f7faff !important" }}>
                                        <div className="card-body">
                                            {this.state.percentageMessage !== "" && (
                                                <div className="form-row">
                                                    <div className="col-12">
                                                        <div className="alert alert-warning text-center" role="alert">
                                                            {this.state.percentageMessage}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="form-row">
                                                <div className="col-12 col-md-4">
                                                    <label htmlFor="buyer">
                                                        Buyer: <span className="text-danger">*</span>
                                                    </label>
                                                    <CustomerSelect
                                                        customerEndpoints={this.props.customerEndpoints!}
                                                        include="BOTH"
                                                        showCreate={true}
                                                        noRedirect={true}
                                                        value={this.state.purchaser}
                                                        onChange={this.purchaserSelected}
                                                        onCreate={this.purchaserSelected}
                                                        isInvalid={this.state.wasValidated && this.state.purchaser === null}
                                                        excludeCustomers={this.props.horseForSalePurchaseList.buyers.map(hfsp => hfsp.purchaser!.id)}
                                                    />
                                                </div>
                                                <InputPercentageGroup
                                                    label="% of Ownership:"
                                                    className="col-12 col-md-4"
                                                    type="number"
                                                    name="percentage"
                                                    isRequired={true}
                                                    disabled={false}
                                                    isInvalid={this.state.wasValidated && this.state.percentageMessage !== ""}
                                                    value={(this.state.percentage && this.state.percentage.toString()) || ""}
                                                    onChange={e => this.setState({ percentage: e.target.value })}
                                                    step="0.01"
                                                />
                                                <div className="col-12 col-md-4">
                                                    <label className="form-check-label ml-2" htmlFor="">
                                                        Additional Information:
                                                    </label>
                                                    <div className="form-group form-check ml-2 mt-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="trainer"
                                                            checked={this.state.trainer}
                                                            required={false}
                                                            onChange={() => this.setState({ trainer: !this.state.trainer })}
                                                        />
                                                        <label className="form-check-label not-required ml-2" htmlFor="trainer">
                                                            Is a Trainer
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-row">
                                                <TextareaGroup
                                                    label="Notes"
                                                    rows={2}
                                                    isRequired={false}
                                                    name="notes"
                                                    className="form-group col-md-12 col-lg-8"
                                                    value={this.state.notes || ""}
                                                    onChange={e => this.setState({ notes: e.target.value })}
                                                />
                                            </div>
                                            <div className="form-row">
                                                <div className="col-12 text-right">
                                                    <button type="button" className="btn btn-secondary" onClick={() => this.clearForm()}>
                                                        <FontAwesomeIcon icon={faBan} className="mr-2" />
                                                        Cancel
                                                    </button>
                                                    <button type="submit" className="btn btn-primary mr-0 ml-3">
                                                        <FontAwesomeIcon icon={faUser} className="mr-2" />
                                                        {this.state.editingPurshaserId ? "Edit" : "Add"} Buyer
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                    <div className="form-row my-3">
                        <div className="col-12">
                            <div className="table-responsive">
                                {/* <BootstrapTable
                                    keyField="purchaser.id"
                                    remote={true}
                                    hover={true}
                                    bordered={false}
                                    className="table-sm"
                                    columns={columns}
                                    bootstrap4={true}
                                    data={this.props.horseForSalePurchaseList.buyers
                                        .sort(SortFn(this.props.winningBidder, this.props.currentHorseForSale.sale.id))}
                                    noDataIndication={() => <div><span>No results found.</span></div>}
                                /> */}
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Buyer(s)</th>
                                            <th>% of Ownership</th>
                                            <th>Notes</th>
                                            <th>Trainer</th>
                                            <th>Winning Bidder is Agent</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map.length === 0 ? (
                                            <tr>
                                                <td>No buyers selected.</td>
                                            </tr>
                                        ) : (
                                            data.map(hfsp => {
                                                const isPurchaserWinningBidder = this.props.winningBidder.id === hfsp.purchaser!.id;
                                                return (
                                                    <tr key={hfsp.purchaser!.id}>
                                                        <td className={isPurchaserWinningBidder ? "text-info table-bordered-td-bg" : ""}>
                                                            {this.handleRenderPurchasers(hfsp)}
                                                        </td>
                                                        <td className={isPurchaserWinningBidder ? "text-info table-bordered-td-bg" : ""}>
                                                            {hfsp.percentage}
                                                        </td>
                                                        <td className={isPurchaserWinningBidder ? "text-info table-bordered-td-bg" : ""}>
                                                            {hfsp.notes}
                                                        </td>
                                                        <td className={isPurchaserWinningBidder ? "text-info table-bordered-td-bg" : ""}>
                                                            {hfsp.trainer === true ? "Yes" : "No"}
                                                        </td>
                                                        <td className={isPurchaserWinningBidder ? "text-info table-bordered-td-bg" : ""}>
                                                            {this.handleRenderAgentAuthorization(hfsp)}
                                                        </td>
                                                        <td className={isPurchaserWinningBidder ? "text-info table-bordered-td-bg" : ""}>
                                                            <div className="btn-group btn-group-toggle">
                                                                <button
                                                                    onClick={() =>
                                                                        this.setState({
                                                                            ...EditableHorseForSalePurchase.PurchaseFromModel(hfsp),
                                                                            editingPurshaserId: hfsp.purchaser!.id
                                                                        })
                                                                    }
                                                                    id={`tooltipEdit${hfsp.purchaser!.id}`}
                                                                    className="btn btn-secondary btn-sm"
                                                                >
                                                                    <FontAwesomeIcon icon={faPenSquare} />
                                                                </button>
                                                                <button
                                                                    onClick={() => this.removePurchaser(hfsp.purchaser!)}
                                                                    id={`tooltipDelete${hfsp.purchaser!.id}`}
                                                                    className="btn btn-secondary btn-sm"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrashAlt} />
                                                                </button>
                                                                <UncontrolledTooltip
                                                                    placement="top"
                                                                    target={`tooltipEdit${hfsp.purchaser!.id}`}
                                                                    delay={0}
                                                                    className="tooltipstrap"
                                                                >
                                                                    Edit Buyer
                                                                </UncontrolledTooltip>
                                                                <UncontrolledTooltip
                                                                    placement="top"
                                                                    target={`tooltipDelete${hfsp.purchaser!.id}`}
                                                                    delay={0}
                                                                    className="tooltipstrap"
                                                                >
                                                                    Remove Buyer
                                                                </UncontrolledTooltip>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div className="form-row pr-0 text-right">
                        <div className="col-12">
                            {this.props.showGoBackHorse && (
                                <button type="button" className="btn btn-secondary" onClick={() => this.props.goBackHorse()}>
                                    Cancel
                                </button>
                            )}
                            <button
                                form="agentAuthorization"
                                className="btn btn-primary mr-0 ml-3"
                                onClick={() => this.submitBuyers()}
                                disabled={!percentageAt100}
                            >
                                Go to Credit & Insurance
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private calculatePercentage(): number {
        return (
            Math.round(
                this.props.horseForSalePurchaseList.buyers
                    .filter(hfsp => hfsp.purchaser && hfsp.purchaser.id !== this.state.editingPurshaserId)
                    .reduce((amount, hfsp) => amount + parseFloat(hfsp.percentage!), 0) * 100
            ) / 100
        );
    }

    private formValidation = (): boolean => {
        const { purchaser } = this.state;
        const totalPercentage = this.calculatePercentage();
        const percentage = parseInt(this.state.percentage, 10);
        if (purchaser !== null && percentage !== undefined) {
            if (percentage && (percentage < 1 || percentage > 100)) {
                this.setState({ percentageMessage: "Please provide a percentage between 1 and 100." });
                return false;
            } else if (percentage && totalPercentage + percentage > 100) {
                this.setState({ percentageMessage: "The total ownership percentage cannot be more than 100%." });
                return false;
            } else {
                this.setState({ percentageMessage: "" });
                return true;
            }
        } else {
            return false;
        }
    };
}



// WEBPACK FOOTER //
// ./src/components/checkout/CheckoutBuyers.tsx